import { formatDateNumber } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ExtendedImmunization } from '@seen/core/models/immunizations/ExtendedImmunization'
import { maybeGetReferenceString } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import type {
  CairRetractionOutcome,
  MarkImmunizationEnteredInErrorRequest,
  MarkImmunizationEnteredInErrorResponse,
} from '@seen/request-types/markImmunizationEnteredInError'
import { Badge, Separator } from '@seen/ui'
import { IconTrash, IconVaccine } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { isAxiosError } from 'axios'
import { compact } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Activity } from '#src/components/activity/Activity.tsx'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { ImmunizationActivity } from '#src/components/patient/immunization/ImmunizationActivity.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCurrentParticipant } from '#src/contexts/index.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { toast } from '#src/utils/toast.ts'

const CAIR_OUTCOME_SUFFIX: Record<CairRetractionOutcome, string> = {
  retracted: ' and retracted from CAIR',
  'not-in-cair': '; CAIR had no matching dose to retract',
  'not-sent': '',
}

function apiErrorMessage(cause: unknown): string {
  if (isAxiosError(cause) && typeof cause.response?.data === 'string') {
    return cause.response.data
  }
  return cause instanceof Error ? cause.message : 'Unknown error'
}

export function ImmunizationDetail(): JSX.Element {
  const { immunizationId } = useParams<{ immunizationId: string }>()
  const participant = useCurrentParticipant()
  const navigate = useNavigate()

  const deleteEnabled = useFeatureFlag(featureFlags.immunizationEnteredInError)
  const isReadOnly = useIsReadOnly()
  const confirmWithAnnotation = useConfirmWithAnnotationStore(
    (state) => state.open
  )
  const { seenApi } = useSeenApi()
  const queryClient = useQueryClient()

  const { data: immunization, isLoading } = useRead(
    ExtendedImmunization,
    immunizationId ?? ''
  )

  const { data: relatedImmunizations } = useSearch(
    ExtendedImmunization,
    {
      patient: participant.reference,
      'vaccine-code': immunization?.vaccineCode,
      status: 'completed',
      _sort: '-date',
    },
    {
      query: {
        enabled: !!participant.reference && !!immunization?.vaccineCode,
      },
    }
  )

  const otherImmunizations = relatedImmunizations?.filter(
    (imm) => imm.id !== immunizationId
  )

  const columns = useMemo<ColumnDef<ExtendedImmunization>[]>(
    () => [
      {
        id: 'vaccine',
        header: 'Vaccine',
        accessorFn: (immunization) => immunization.vaccineDisplay,
        cell: ({ row }) => (
          <div className="font-medium">{row.original.vaccineDisplay}</div>
        ),
      },
      {
        id: 'administeredDate',
        header: 'Administered',
        accessorFn: (immunization) => immunization.administeredDate,
        cell: ({ row }) =>
          row.original.administeredDate ? (
            <span>{formatDateNumber(row.original.administeredDate)}</span>
          ) : null,
      },
      {
        id: 'lotNumber',
        header: 'Lot',
        accessorFn: (immunization) => immunization.lotNumber,
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.lotNumber ?? '—'}
          </span>
        ),
      },
    ],
    []
  )

  if (isLoading) {
    return <Loader />
  }

  if (!immunization) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <IconVaccine className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-medium text-foreground">
            Immunization not found
          </h3>
          <p className="mb-4 text-muted-foreground">
            The requested immunization could not be found.
          </p>
        </div>
      </div>
    )
  }

  const isRefused = immunization.isNotDone && immunization.statusReason
  const refusalReasonCode = immunization.statusReason?.coding?.[0]?.code
  const refusalReasonDisplay =
    immunization.statusReason?.coding?.[0]?.display ??
    immunization.refusalReason

  const badges = compact([
    isRefused && (
      <Badge key="refused" variant="outline" className="text-xs">
        Refused
      </Badge>
    ),
    immunization.patientConsent !== undefined && (
      <Badge
        key="consent"
        variant={immunization.patientConsent ? 'default' : 'secondary'}
        className="text-xs"
      >
        Consent: {immunization.patientConsent ? 'Yes' : 'No'}
      </Badge>
    ),
    immunization.allowedSharing !== undefined && (
      <Badge
        key="sharing"
        variant={immunization.allowedSharing ? 'default' : 'secondary'}
        className="text-xs"
      >
        Sharing: {immunization.allowedSharing ? 'Yes' : 'No'}
      </Badge>
    ),
  ])

  const label = immunization.vaccineDisplay ?? 'immunization'
  const enteredInErrorReason = immunization.enteredInErrorReason
  const enteredInErrorAuthor = immunization.enteredInErrorAuthor

  const handleDelete = (): void => {
    confirmWithAnnotation({
      title: `Delete "${label}"?`,
      description:
        'This marks the immunization as entered in error and retracts the dose from CAIR if the registry holds it. If CAIR cannot confirm the retraction, the immunization is left unchanged so you can try again.',
      noteRequired: true,
      noteLabel: 'Reason',
      onConfirm: (reason) => {
        const request: MarkImmunizationEnteredInErrorRequest = {
          immunizationId: immunization.id,
          reason: reason.trim(),
        }
        const deletion = seenApi
          .post<MarkImmunizationEnteredInErrorResponse>(
            '/immunization/entered-in-error',
            request
          )
          .then(({ data }) => {
            cacheKeys.invalidateResourceType(queryClient, 'Immunization')
            cacheKeys.invalidateResourceType(queryClient, 'Communication')
            void navigate(`/Patient/${participant.id}/immunizations`)
            return data
          })
        toast.promise(deletion, {
          loading: `Deleting ${label}…`,
          success: ({ cairOutcome }) =>
            `${label} deleted${CAIR_OUTCOME_SUFFIX[cairOutcome]}`,
          error: (cause: unknown) =>
            `Failed to delete ${label}: ${apiErrorMessage(cause)}`,
        })
      },
    })
  }

  return (
    <div className="space-y-4 max-w-4xl py-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">
              {immunization.vaccineDisplay}
            </h1>
            {isRefused && <Badge variant="outline">Refused</Badge>}
            {immunization.isEnteredInError && (
              <Badge variant="destructive">Entered in error</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <DevMedplumAppLinkOverlay to={immunization} />
          {deleteEnabled && !isReadOnly && !immunization.isEnteredInError && (
            <MoreDropdownMenu>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onSelect={handleDelete}
              >
                <IconTrash className="-mt-px size-4 text-foreground-secondary" />
                Delete…
              </DropdownMenuItem>
            </MoreDropdownMenu>
          )}
        </div>
      </div>

      {enteredInErrorReason && (
        <div className="space-y-1 rounded-md border border-destructive/40 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-1 text-muted-foreground">
            <span>Marked entered in error</span>
            {enteredInErrorAuthor && (
              <>
                <span>by</span>
                <PractitionerDisplay practitioner={enteredInErrorAuthor} />
              </>
            )}
            {enteredInErrorReason.time && (
              <span>on {formatDateNumber(enteredInErrorReason.time)}</span>
            )}
          </div>
          <div className="text-foreground">{enteredInErrorReason.text}</div>
        </div>
      )}

      <div className="flex flex-col items-start gap-1.5">
        <span className="text-xs font-medium">Date Administered</span>
        <ResourceFieldForm
          instance={immunization}
          valueFn={(i) => i.administeredDateString}
          patchFn={(i, value) => {
            invariant(value, 'Administered date is required')
            i.administeredDate = value
          }}
          render={({ field: { onChange, ...fieldRest } }) => (
            <DatePicker
              {...fieldRest}
              // The popover's native date input emits an empty value when
              // cleared; the administered date is required, so drop the change
              // and let the controlled input snap back to the stored day.
              onChange={(date) => {
                if (date) {
                  onChange(date)
                }
              }}
              placeholder="Administered date"
              dateFormatter={formatDateNumber}
              clearText={null}
            />
          )}
        />
      </div>

      {isRefused && (
        <div className="space-y-1 text-sm">
          <span className="text-muted-foreground">Refusal Reason: </span>
          <span className="text-foreground">
            {refusalReasonDisplay ?? refusalReasonCode}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 text-sm">
        {immunization.lotNumber && (
          <div>
            <span className="text-muted-foreground">Lot: </span>
            <span className="text-foreground">{immunization.lotNumber}</span>
          </div>
        )}
        {immunization.manufacturer && (
          <div>
            <span className="text-muted-foreground">Manufacturer: </span>
            <span className="text-foreground">{immunization.manufacturer}</span>
          </div>
        )}
        {immunization.site && (
          <div>
            <span className="text-muted-foreground">Site: </span>
            <span className="text-foreground">{immunization.site}</span>
          </div>
        )}
        {immunization.route && (
          <div>
            <span className="text-muted-foreground">Route: </span>
            <span className="text-foreground">{immunization.route}</span>
          </div>
        )}
        {immunization.doseQuantity && (
          <div>
            <span className="text-muted-foreground">Dose: </span>
            <span className="text-foreground">
              {immunization.doseQuantity.value} {immunization.doseQuantity.unit}
            </span>
          </div>
        )}
        {immunization.series && (
          <div>
            <span className="text-muted-foreground">Series: </span>
            <span className="text-foreground">{immunization.series}</span>
          </div>
        )}
      </div>

      {immunization.performers.length > 0 && (
        <div className="grid grid-cols-2 gap-4 text-sm">
          {immunization.performers.map((performer, index) => {
            const actor = performer.actor
            if (!isOfType(actor, 'Practitioner')) {
              return null
            }

            return (
              <div
                key={`${performer.function ?? ''}-${maybeGetReferenceString(actor) ?? referenceDisplay(actor) ?? 'performer'}-${index}`}
              >
                <span className="text-muted-foreground">
                  {performer.function ?? 'Performer'}:
                </span>
                <span className="ml-1 text-foreground">
                  <PractitionerDisplay practitioner={actor} />
                </span>
              </div>
            )
          })}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 text-sm">
        {immunization.vfcEligibility && (
          <div>
            <span className="text-muted-foreground">VFC: </span>
            <span className="text-foreground">
              {immunization.vfcEligibility}
            </span>
          </div>
        )}
        {immunization.fundingSource && (
          <div>
            <span className="text-muted-foreground">Funding: </span>
            <span className="text-foreground">
              {immunization.fundingSource}
            </span>
          </div>
        )}
        {immunization.informationSource && (
          <div>
            <span className="text-muted-foreground">Source: </span>
            <span className="text-foreground">
              {immunization.informationSource}
            </span>
          </div>
        )}
        {immunization.publicityCode && (
          <div>
            <span className="text-muted-foreground">Publicity: </span>
            <span className="text-foreground">
              {immunization.publicityCode}
            </span>
          </div>
        )}
        {immunization.priorityPopulation && (
          <div>
            <span className="text-muted-foreground">Priority: </span>
            <span className="text-foreground">
              {immunization.priorityPopulation}
            </span>
          </div>
        )}
        {immunization.ndc && (
          <div>
            <span className="text-muted-foreground">NDC: </span>
            <span className="text-foreground">{immunization.ndc}</span>
          </div>
        )}
      </div>

      {immunization.visDocumentType && (
        <div className="text-sm">
          <span className="text-muted-foreground">VIS: </span>
          <span className="text-foreground">
            {immunization.visDocumentType}
          </span>
        </div>
      )}

      <div>
        <h3 className="mb-2 text-sm font-medium text-muted-foreground">
          Notes
        </h3>
        <ResourceFieldForm<ExtendedImmunization, string>
          debounceTyping
          instance={immunization}
          valueFn={() => immunization.notes ?? ''}
          patchFn={(instance, value) => {
            instance.notes = value
          }}
          render={({ field: { value, onChange, ...fieldRest } }) => (
            <RichTextEditor
              {...fieldRest}
              value={value}
              onChange={onChange}
              variant="underline"
            />
          )}
        />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium text-muted-foreground">
          Reason
        </h3>
        <ResourceFieldForm<ExtendedImmunization, string>
          debounceTyping
          instance={immunization}
          valueFn={() => immunization.reasonText ?? ''}
          patchFn={(instance, value) => {
            instance.reasonText = value
          }}
          render={({ field: { value, onChange, ...fieldRest } }) => (
            <RichTextEditor
              {...fieldRest}
              value={value}
              onChange={onChange}
              variant="underline"
            />
          )}
        />
      </div>

      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2">{badges}</div>
      )}

      {otherImmunizations && otherImmunizations.length > 0 && (
        <div className="border-t pt-4">
          <h3 className="mb-3 text-sm font-medium text-foreground">
            Other {immunization.vaccineDisplay} Immunizations
          </h3>
          <DataTable
            columns={columns}
            data={otherImmunizations}
            onRowClick={(imm) => {
              void navigate(
                `/Patient/${participant.id}/immunizations/${imm.id}`
              )
            }}
          />
        </div>
      )}

      <Separator />
      <Activity resource={immunization} subject={immunization.patient}>
        <ImmunizationActivity immunization={immunization} />
      </Activity>
    </div>
  )
}
